import type {Dayjs} from "dayjs";
import type {PublicRegistrationLabelKey} from "../../i18n/publicPages";

export type PublicRegistrationLanguage = "hr" | "en";
export type PublicRegistrationMode = "single" | "bulk";

export type PublicEventFormResponse = {
    eventId: number;
    eventCode: string;
    eventName: string;
    eventNameHr?: string | null;
    eventNameEn?: string | null;
    eventPlace?: string | null;
    startDate: string;
    endDate: string;
    lifecycleStatus?: string | number | null;
    isArchived?: boolean | null;
    registrationUnavailableReason?: string | null;
    hasParticipationFee: boolean;
    logoUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    publicAssetMode?: string | number | null;
    programPdfUrl?: string | null;
    venueLayoutImageUrl?: string | null;
    template?: {
        fields: PublicFormField[];
    } | null;
    paymentMethods?: PublicPaymentMethodOptions[];
    participationFees?: PublicParticipationFeeOption[];
    registrationOptions?: PublicRegistrationOption[];
    roomAvailability?: PublicRoomAvailability[];
};

export type PublicFormField = {
    id: number;
    code: string;
    label: string;
    type: string | number;
    isRequired: boolean;
    order: number;
    options?: PublicFormFieldOption[];
};

type PublicFormFieldOption = {
    id: number;
    value: string;
    label: string;
    order: number;
};

export type PublicPaymentMethodOptions = {
    category: string | number;
    methods: Array<string | number>;
};

export type PublicParticipationFeeOption = {
    feePriceId: number;
    feeTypeId: number;
    labelHr: string;
    labelEn: string;
    pricePeriodName?: string | null;
    amount: number;
    isFree: boolean;
    isSponsored: boolean;
    requiresParticipantTypeProof?: boolean;
};

export type PublicRegistrationOption = {
    id: number;
    labelHr: string;
    labelEn: string;
    descriptionHr?: string | null;
    descriptionEn?: string | null;
    questionType: string | number;
    priceEffect: string | number;
    price: number;
    allowedFeeTypeIds?: number[];
    visibilityDependsOnEventOptionId?: number | null;
    visibilityDependsOnExpectedAnswer?: boolean | null;
    choices?: PublicRegistrationOptionChoice[];
};

export type PublicRegistrationOptionChoice = {
    id: number;
    value: string;
    labelHr: string;
    labelEn: string;
    order: number;
};

export type PublicRoomAvailability = {
    hotelId: number;
    hotelName: string;
    roomTypeId: number;
    roomTypeName: string;
    roomCapacity: number;
    pricePerNight: number;
    nights: PublicRoomNightAvailability[];
};

type PublicRoomNightAvailability = {
    night: string;
    totalRooms: number;
    reservedRooms: number;
    remainingRooms: number;
};

export type PublicRegistrationResponse = {
    registrationId: number;
    participantId?: number;
    verificationRequired: boolean;
    verificationTokenExpiresAtUtc?: string | null;
};

export type PublicRegistrationBulkSubmissionResult = {
    rowNumber: number;
    participantName: string;
    email: string;
    status: "accepted" | "rejected";
    verificationTokenExpiresAtUtc?: string | null;
    error?: string;
};

export type PublicRegistrationFormValues = {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    company?: string;
    linkedInUrl?: string;
    payerType?: PayerTypeValue;
    payerName?: string;
    payerEmail?: string;
    payerPhone?: string;
    payerAddress?: string;
    payerCity?: string;
    payerCountry?: string;
    payerTaxId?: string;
    payerContactPerson?: string;
    answers?: Record<string, any>;
    activityAnswers?: Record<string, any>;
    selectedParticipationFeePriceId?: number;
    participantTypeProofFileId?: number;
    participantTypeProofFileName?: string;
    selectedOptionIds?: number[];
    participationPaymentMethod?: PaymentMethodValue;
    reserveAccommodation?: boolean;
    accommodationSelectionKey?: string;
    accommodationArrivalDate?: Dayjs;
    accommodationDepartureDate?: Dayjs;
    accommodationRoomCount?: number;
    accommodationPaymentMethod?: PaymentMethodValue;
    gdprConsentAccepted?: boolean;
    bulkRows?: PublicRegistrationBulkRowValues[];
};

export type PublicRegistrationBulkRowValues = {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    company?: string;
    linkedInUrl?: string;
    additionalInfo?: string;
    answers?: Record<string, any>;
    selectedOptionIds?: number[];
    activityAnswers?: Record<string, any>;
};

export type PublicRegistrationBulkCsvImportResult = {
    rows: PublicRegistrationBulkRowValues[];
    unknownColumns: string[];
    unsupportedAnswerColumns: string[];
    unsupportedActivityColumns: string[];
    formatError: boolean;
    dataOnMarkerRow?: boolean;
};

export type PublicRegistrationDraft = {
    version: number;
    mode?: PublicRegistrationMode;
    values?: Record<string, any>;
    savedAtUtc?: string;
};

export type PublicRegistrationSubmissionContext = {
    email: string;
    participantName: string;
    summaryItems: Array<{
        label: string;
        amount: number;
    }>;
};

export type PublicEventAssetLink = {
    key: string;
    label: string;
    href: string;
};

type PublicRegistrationBulkParticipantField = Exclude<
    keyof PublicRegistrationBulkRowValues,
    "answers" | "selectedOptionIds" | "activityAnswers"
>;

export type PublicRegistrationSubmitPayload = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    company: string;
    linkedInUrl: string;
    additionalInfo: string;
    payerType: PayerTypeValue;
    payerName: string;
    payerEmail: string;
    payerPhone: string;
    payerAddress: string;
    payerCity: string;
    payerCountry: string;
    payerTaxId: string;
    payerContactPerson: string;
    accommodationHotelId?: number | null;
    accommodationRoomTypeId?: number | null;
    accommodationArrivalDate?: string | null;
    accommodationDepartureDate?: string | null;
    accommodationRoomCount?: number | null;
    participantTypeProofFileId?: number | null;
    participationPaymentMethod: PaymentMethodValue;
    accommodationPaymentMethod: PaymentMethodValue;
    gdprConsentAccepted: boolean;
    items: Array<{
        description: string;
        quantity: number;
        unitPrice: number;
        isAccommodation: boolean;
        itemType?: number;
        sourceType?: number;
        sourceId?: number | null;
    }>;
    answers: Array<{
        code: string;
        value: string;
    }>;
    activityAnswers: Array<{
        eventOptionId: number;
        isSelected: boolean;
        textValue?: string;
        selectedChoiceIds: number[];
    }>;
};

export const isPayableRegistrationItem = (item: PublicRegistrationSubmitPayload["items"][number]) =>
    item.quantity > 0 && item.unitPrice > 0;

export type PublicFormFieldTypeName =
    | "Text"
    | "TextArea"
    | "Number"
    | "Date"
    | "Email"
    | "Phone"
    | "Checkbox"
    | "Select"
    | "MultiSelect";

export type PublicRegistrationOptionQuestionTypeName =
    | "YesNo"
    | "SingleChoice"
    | "MultiChoice"
    | "Text";

export const fieldTypeNames: PublicFormFieldTypeName[] = [
    "Text",
    "TextArea",
    "Number",
    "Date",
    "Email",
    "Phone",
    "Checkbox",
    "Select",
    "MultiSelect"
];

export const PUBLIC_REGISTRATION_DRAFT_VERSION = 1;
export const PUBLIC_REGISTRATION_DRAFT_STORAGE_PREFIX = "eventflow.public-registration-draft";

export const PaymentMethodValue = {
    BankTransfer: 0,
    Card: 1,
    Sponsor: 2,
    Pos: 3,
    Cash: 4
} as const;

export type PaymentMethodValue = typeof PaymentMethodValue[keyof typeof PaymentMethodValue];

export const PaymentMethodCategoryValue = {
    Participation: 0,
    Accommodation: 1
} as const;

export type PaymentMethodCategoryValue = typeof PaymentMethodCategoryValue[keyof typeof PaymentMethodCategoryValue];

export const EventOfferLineItemTypeValue = {
    Participation: 0,
    Option: 1,
    Accommodation: 2
} as const;

export const EventOfferLineSourceTypeValue = {
    None: 0,
    EventFeePrice: 1,
    EventOption: 2,
    EventAccommodation: 3,
    EventRoomReservation: 4
} as const;

export const PayerTypeValue = {
    Individual: 0,
    Company: 1,
    SponsoredParticipation: 2
} as const;

export type PayerTypeValue = typeof PayerTypeValue[keyof typeof PayerTypeValue];

export const requiresCompanyStylePayerFields = (payerType?: PayerTypeValue): boolean =>
    payerType === PayerTypeValue.Company || payerType === PayerTypeValue.SponsoredParticipation;

export const buildPayerTypeOptions = (labels: Record<string, string>) => [
    {value: PayerTypeValue.Individual, label: labels.individual},
    {value: PayerTypeValue.Company, label: labels.companyPayer},
    {value: PayerTypeValue.SponsoredParticipation, label: labels.sponsoredParticipation}
];

export const bulkParticipantColumnMap: Record<string, PublicRegistrationBulkParticipantField> = {
    first_name: "firstName",
    last_name: "lastName",
    email: "email",
    phone_number: "phoneNumber",
    company: "company",
    linkedin_url: "linkedInUrl",
    additional_info: "additionalInfo"
};

export const paymentMethodNameToValue: Record<string, PaymentMethodValue> = {
    BankTransfer: PaymentMethodValue.BankTransfer,
    Card: PaymentMethodValue.Card,
    Sponsor: PaymentMethodValue.Sponsor,
    Pos: PaymentMethodValue.Pos,
    Cash: PaymentMethodValue.Cash
};

export const paymentCategoryNameToValue: Record<string, PaymentMethodCategoryValue> = {
    Participation: PaymentMethodCategoryValue.Participation,
    Accommodation: PaymentMethodCategoryValue.Accommodation
};

export type PublicRegistrationLabels = Record<PublicRegistrationLabelKey, string>;
